import type { GpcChatSessionView } from '../GpcChat/types';

type GpcAmbiguousChoiceId = 'confirmed' | 'far_qa' | 'revise';
type GpcAmbiguousOption = { id: GpcAmbiguousChoiceId; label: string };

export type GpcRouterResponse =
  | {
      kind: 'start_confirmation';
      originalMessage: string;
      prompt: string;
      options: string[];
      handoffId?: string;
      homeChatSessionId?: string;
    }
  | {
      kind: 'ambiguous_choice';
      originalMessage: string;
      prompt: string;
      options: GpcAmbiguousOption[];
      handoffId?: string;
      homeChatSessionId?: string;
    }
  | { kind: 'chat'; view: GpcChatSessionView }
  | { kind: 'revision'; homeChatSessionId?: string }
  | { kind: 'resume_selection'; prompt: string; options: unknown[] };

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function ambiguousOptionId(value: unknown): GpcAmbiguousChoiceId | null {
  if (value === 'confirmed' || value === 'far_qa' || value === 'revise') return value;
  return null;
}

function ambiguousOptions(value: unknown): GpcAmbiguousOption[] {
  if (!Array.isArray(value)) return [
    { id: 'confirmed', label: 'Start GPC purchase' },
    { id: 'far_qa', label: 'Ask FAR/RFO question' },
    { id: 'revise', label: 'Revise request' },
  ];
  const options: GpcAmbiguousOption[] = [];
  for (const option of value) {
    const item = asRecord(option);
    const id = ambiguousOptionId(item?.id);
    if (id) options.push({ id, label: typeof item?.label === 'string' ? item.label : id });
  }
  return options.length ? options : ambiguousOptions(undefined);
}

/**
 * Recognizes only the server's Home Chat router envelopes. The client never
 * infers a GPC transition from prose or a bare intent; `gpc_chat` must carry
 * the controller view produced after the server-side confirmation gate.
 */
export function extractGpcRouterResponse(value: unknown): GpcRouterResponse | null {
  const root = asRecord(value);
  if (!root) return null;
  if (root.kind === 'start_confirmation' || root.kind === 'ambiguous_choice' || root.kind === 'chat' || root.kind === 'revision' || root.kind === 'resume_selection') {
    return value as GpcRouterResponse;
  }
  if (typeof root.status !== 'string') return null;
  if (root.status === 'gpc_start_confirmation') {
    const handoff = asRecord(root.gpc_handoff);
    const originalMessage = root.original_message;
    const prompt = handoff?.prompt;
    if (typeof originalMessage !== 'string' || typeof prompt !== 'string') return null;
    // Opaque, owner-bound identifier. The client only relays it; it never
    // constructs a handoff token or changes the server's original message.
    const handoffId = typeof handoff?.handoff_id === 'string'
      ? handoff.handoff_id
      : typeof root.handoff_id === 'string' ? root.handoff_id : undefined;
    const homeChatSessionId = typeof root.home_chat_session_id === 'string'
      ? root.home_chat_session_id
      : undefined;
    if (root.intent === 'ambiguous') {
      return {
        kind: 'ambiguous_choice',
        originalMessage,
        prompt,
        options: ambiguousOptions(handoff?.options),
        ...(handoffId ? { handoffId } : {}),
        ...(homeChatSessionId ? { homeChatSessionId } : {}),
      };
    }
    return {
      kind: 'start_confirmation',
      originalMessage,
      prompt,
      options: Array.isArray(handoff?.options)
        ? handoff!.options.filter((option): option is string => typeof option === 'string')
        : ['confirmed', 'revise'],
      ...(handoffId ? { handoffId } : {}),
      ...(homeChatSessionId ? { homeChatSessionId } : {}),
    };
  }
  if (root.status === 'gpc_chat' || root.status === 'workflow_review_ready') {
    const view = asRecord(root.gpc_chat);
    if (!view || typeof view.session_id !== 'string') return null;
    // A lazy Home Chat handoff can mint its parent session during the same
    // response. Preserve that server-issued id on the controller view so the
    // next bound GPC turn and any later reopening target the durable mapping.
    const homeChatSessionId = typeof root.home_chat_session_id === 'string'
      ? root.home_chat_session_id
      : typeof view.home_chat_session_id === 'string' ? view.home_chat_session_id : undefined;
    return {
      kind: 'chat',
      view: {
        ...(view as unknown as GpcChatSessionView),
        ...(homeChatSessionId ? { home_chat_session_id: homeChatSessionId } : {}),
      },
    };
  }
  if (root.status === 'gpc_resume_selection') {
    const handoff = asRecord(root.gpc_handoff);
    return {
      kind: 'resume_selection',
      prompt: typeof handoff?.prompt === 'string' ? handoff.prompt : 'Choose a GPC purchase to resume.',
      options: Array.isArray(handoff?.options) ? handoff!.options : [],
    };
  }
  if (root.status === 'gpc_revision') {
    return {
      kind: 'revision',
      ...(typeof root.home_chat_session_id === 'string'
        ? { homeChatSessionId: root.home_chat_session_id }
        : {}),
    };
  }
  return null;
}

/** Type guard used at the Home Chat seam before any Q&A-only branch. */
export function isGpcRouterResponse(value: unknown): value is GpcRouterResponse {
  const root = asRecord(value);
  if (!root) return false;
  if (root.kind === 'start_confirmation' || root.kind === 'ambiguous_choice' || root.kind === 'chat' || root.kind === 'revision' || root.kind === 'resume_selection') {
    return true;
  }
  return extractGpcRouterResponse(value) !== null;
}
